<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Teachers Management
            </h1>
            <div class="flex flex-wrap items-center gap-3">
                <button @click="openImportModal" class="btn btn-success">
                    Import Teacher
                </button>
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Teacher
                </button>
            </div>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name, NIP, or specialization..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Specialization -->
                    <SearchableSelect
                        v-model="filters.specialization"
                        :options="specializationOptions"
                        placeholder="All Specializations"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchTeachers"
                    />

                    <!-- Status -->
                    <SearchableSelect
                        v-model="filters.is_homeroom"
                        :options="statusOptions"
                        placeholder="All Teachers"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchTeachers"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="teacherStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="6" />
        </div>

        <!-- Error State -->
        <div
            v-if="teacherStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ teacherStore.error }}
                    </p>
                    <button
                        @click="teacherStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!teacherStore.loading && !teacherStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Teacher ID
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                NIP
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Specialization
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="teacher in teacherStore.teachers"
                            :key="teacher.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex items-center">
                                    <div class="m-0">
                                        <div
                                            class="text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {{
                                                teacher.user?.name || "Unknown"
                                            }}
                                        </div>
                                        <div
                                            class="text-sm text-gray-500 dark:text-gray-400"
                                        >
                                            {{
                                                teacher.user?.email ||
                                                "No email"
                                            }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ teacher.teacher_id }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ teacher.nip }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ teacher.specialization || "-" }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="teacher.is_homeroom"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Homeroom
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                                >
                                    Regular
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editTeacher(teacher)"
                                    title="Edit Teacher"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(teacher)"
                                    title="Delete Teacher"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="teacherStore.teachers.length === 0"
                :icon="Users"
                title="No teachers found"
                description="Get started by adding a new teacher."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Teacher
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="teacherStore.meta"
                :per-page="teacherStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Teacher Modal -->
        <TeacherModal
            v-if="showModal"
            :teacher="selectedTeacher"
            :is-editing="isEditing"
            @close="closeModal"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />

        <!-- Import Teacher Modal -->
        <ImportTeacherModal
            v-if="showImportModal"
            @close="closeImportModal"
            @success="handleImportSuccess"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useTeacherStore } from "@/stores/teacherStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import TeacherModal from "@/components/TeacherModal.vue";
import ImportTeacherModal from "@/components/ImportTeacherModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import type { Teacher } from "@/api/teacher";
import { Plus, RotateCcw, Pencil, Trash2, Users } from "@lucide/vue";

const teacherStore = useTeacherStore();
// State
const searchQuery = ref("");
const filters = ref({
    specialization: "",
    is_homeroom: undefined as boolean | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const showImportModal = ref(false);
const selectedTeacher = ref<Teacher | null>(null);
const isEditing = ref(false);
const teacherToDelete = ref<Teacher | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this teacher? This action cannot be undone.",
);

// Computed
const specializations = computed(() => {
    const specs = new Set(
        teacherStore.teachers
            .map((t: Teacher) => t.specialization)
            .filter(Boolean),
    );
    return Array.from(specs).sort();
});

// Options for Specialization filter
const specializationOptions = computed(() => {
    const options = [{ value: "", label: "All Specializations" }];
    specializations.value.forEach((spec: string | undefined) => {
        if (spec) {
            options.push({ value: spec, label: spec });
        }
    });
    return options;
});

// Options for Status filter
const statusOptions = computed(() => [
    { value: undefined, label: "All Teachers" },
    { value: true, label: "Homeroom Teachers" },
    { value: false, label: "Regular Teachers" },
]);

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.specialization !== "" ||
        filters.value.is_homeroom !== undefined ||
        teacherStore.page !== 1 ||
        teacherStore.perPage !== 10
    );
});

// Methods
const handlePerPageChange = (value: number) => {
    teacherStore.perPage = value;
    teacherStore.page = 1;
    fetchTeachers();
};

const fetchTeachers = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await teacherStore.fetchTeachers(searchFilters);
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { specialization: "", is_homeroom: undefined };
    teacherStore.setPage(1);
    teacherStore.setPerPage(10);
    fetchTeachers();
};

const debouncedSearch = useDebounce(fetchTeachers, 300);

const openModal = () => {
    selectedTeacher.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editTeacher = (teacher: Teacher) => {
    selectedTeacher.value = teacher;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedTeacher.value = null;
    isEditing.value = false;
};

const confirmDelete = (teacher: Teacher) => {
    teacherToDelete.value = teacher;
    deleteModalTitle.value = "Delete Teacher";
    deleteModalMessage.value = `Are you sure you want to delete ${teacher.user?.name || "this teacher"}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    teacherToDelete.value = null;
};

const handleSaved = () => {
    // Refresh the teachers list to show the new/updated teacher
    fetchTeachers();
    showNotification(
        isEditing.value
            ? "Teacher updated successfully"
            : "Teacher created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (teacherToDelete.value) {
            await teacherStore.deleteTeacher(teacherToDelete.value.id);
            showNotification("Teacher deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete teacher", "error");
    }
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImportSuccess = () => {
    showNotification("Teachers imported successfully", "success");
    fetchTeachers();
};

const goToPage = (page: number) => {
    teacherStore.setPage(page);
    fetchTeachers();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchTeachers();
});
</script>
